import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../css/PaymentResult.css';

const API_URL = 'https://craftmystore.vercel.app';

const PaymentStatus = () => {
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState({});
  const [status, setStatus] = useState('processing');
  const [errorDetails, setErrorDetails] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // First, check if it's a cancel redirect
        if (location.pathname.includes('/payment-cancel') ||
          location.search.includes('cancel=true') ||
          location.search.includes('success=false') ||
          location.search.includes('order_status=CANCELLED')) {
          console.log('Payment was cancelled based on URL');
          setStatus('failed');
          setErrorDetails('Payment was cancelled');
          setLoading(false);
          return;
        }

        const urlParams = new URLSearchParams(location.search);

        // Get payment details from URL
        const merchantTransactionId = urlParams.get('merchantTransactionId');
        const orderId = urlParams.get('order_id');
        const method = urlParams.get('method') || 'phonepe';
        const amount = urlParams.get('amount');
        const customer = urlParams.get('customer');

        // Set payment info immediately for display
        setPaymentInfo({
          transactionId: merchantTransactionId || orderId || 'N/A',
          amount: amount || '0',
          method: method,
          customer: customer ? decodeURIComponent(customer) : 'Customer'
        });

        // Check for direct success parameter
        if (urlParams.get('success') === 'true') {
          console.log('Success parameter found in URL');
          setStatus('success');
          setLoading(false);
          return;
        }

        // Check if we have a valid transaction ID to verify
        const transactionId = merchantTransactionId || orderId;
        if (!transactionId) {
          console.error("FATAL: No transaction ID in URL. Cannot verify payment.");
          setStatus('failed');
          setLoading(false);
          return;
        }

        console.log(`Starting payment verification for transaction: ${transactionId}, method: ${method}`);

        // More patient retry strategy
        const verifyWithRetry = async (retryCount = 0, maxRetries = 3) => {
          try {

            // Select the correct verification endpoint based on payment method
            const verifyEndpoint = method === 'phonepe'
              ? `${API_URL}/api/payment/verify-phonepe/${transactionId}`
              : `${API_URL}/api/payment/verify-cashfree/${transactionId}`;

            const response = await axios.get(
              verifyEndpoint,
              { timeout: 20000 }
            );

            const backendStatus = response.data.status;

            if (backendStatus === 'SUCCESS') {
              console.log('✅ Payment confirmed as SUCCESSFUL.');
              setStatus('success');
              setLoading(false);
            } else if (backendStatus === 'PENDING') {
              if (retryCount < maxRetries) {
                // Increased delay for subsequent retries
                const delay = 3000 + (retryCount * 1000);
                console.log(`⏳ Payment is PENDING. Retrying in ${delay / 1000} seconds...`);
                setTimeout(() => verifyWithRetry(retryCount + 1, maxRetries), delay);
              } else {
                console.log('❌ Payment still PENDING after all retries. Marking as FAILED.');
                setStatus('failed');
                setErrorDetails('Payment verification timed out. If money was debited, it will be refunded automatically.');
                setLoading(false);
              }
            } else { // Includes FAILED and any other non-success status
              console.log(`❌ Payment confirmed as FAILED. Reason: ${response.data.message || 'Unknown'}`);
              setStatus('failed');
              setErrorDetails(response.data.message || 'Unknown error occurred');
              setLoading(false);
            }
          } catch (error) {
            console.error("Error during verification API call:", error);
            if (retryCount < maxRetries) {
              const delay = 3000 + (retryCount * 1000);
              console.log(`API error. Retrying in ${delay / 1000} seconds...`);
              setTimeout(() => verifyWithRetry(retryCount + 1, maxRetries), delay);
            } else {
              console.log('❌ API calls failed after all retries. Marking as FAILED.');
              setStatus('failed');
              setErrorDetails(error.response?.data?.message || error.message || 'Server communication error');
              setLoading(false);
            }
          }
        };

        // Start the verification process after a small initial delay
        setTimeout(() => verifyWithRetry(), 1000);
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('failed');
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location]);

  const handleReturnHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="payment-status-container">
        <h1>Verifying Your Payment...</h1>
        <p>Please wait while we confirm your payment status. This may take up to a minute.</p>
        <div className="loading-indicator">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-status-container">
      <h1>Payment {status === 'success' ? 'Successful!' : 'Failed'}</h1>

      {status === 'success' ? (
        <>
          <div className="payment-details">
            <p><strong>Transaction ID:</strong> {paymentInfo.transactionId}</p>
            <p><strong>Amount:</strong> ₹{paymentInfo.amount}</p>
            <p><strong>Payment Method:</strong> {
              paymentInfo.method === 'phonepe' ? 'PhonePe' :
                paymentInfo.method === 'cashfree' ? 'Cashfree' :
                  paymentInfo.method
            }</p>
            <p><strong>Customer:</strong> {paymentInfo.customer}</p>
          </div>

          <p>Thank you for your purchase!</p>
          <p>Our team will contact you shortly to get started on your project.</p>
        </>
      ) : (
        <>
          <p>Sorry, there was an issue with your payment.</p>
          <p>Please try again or contact our support team for assistance.</p>
          <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f3f4f6', borderRadius: '5px' }}>
            <p><small>Transaction ID: {paymentInfo.transactionId}</small></p>
            {errorDetails && <p><small>Error details: {errorDetails}</small></p>}
            <p><small>If money was debited, please contact support with this transaction ID.</small></p>
          </div>
        </>
      )}

      <button
        onClick={handleReturnHome}
        className="home-button"
      >
        Return to Home
      </button>
    </div>
  );
};

export default PaymentStatus;